/**
 * #129 — identity of a selected day-plan badge. Each badge has a unique `id`
 * (`<stepId>-<TYPE>` on a route, `<orderCode>-pickup|delivery` in the
 * Unscheduled pool); `stepId` is null for every unscheduled pseudo-stop, so it
 * can't tell two of them apart.
 */
export function isSameSelectedStep(selected, step, driverId) {
  if (!selected || !step) return false;
  return selected.id === step.id && selected.type === step.type && String(selected.driverId) === String(driverId);
}

const deliverySpot = (order) => {
  if (!order || order.deliveryAddressLat == null || order.deliveryAddressLng == null) return null;
  const lat = Number(order.deliveryAddressLat);
  const lng = Number(order.deliveryAddressLng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return `${lat.toFixed(4)},${lng.toFixed(4)}`;
};

/**
 * #131 — where the legs of orders moved onto a route go. `targetLegs` are the
 * route's `<orderId>_PICKUP|DELIVERY` keys in sequence; `movedOrders` the
 * orders being added; `getOrder(id)` resolves the order of a target leg.
 *
 * A moved order delivered where the route already delivers (CT: every box goes
 * to the depot) has its pickup placed before the first delivery at that place
 * and its delivery after the last one, so the route stays « boxes, then the
 * depot » instead of driving to the depot after every box. Any other order is
 * appended as pickup + delivery, as before. A SERVICE order is a single leg
 * (`_DELIVERY`), appended.
 */
export function insertMovedLegs(targetLegs, movedOrders, getOrder) {
  const legs = [...targetLegs];
  const moved = new Map(movedOrders.map((o) => [o.id, o]));
  const orderOf = (key) => {
    const id = Number(key.split('_')[0]);
    return moved.get(id) || getOrder(id);
  };
  movedOrders.forEach((order) => {
    const pickup = `${order.id}_PICKUP`;
    const delivery = `${order.id}_DELIVERY`;
    if (order.orderType === 'SERVICE') {
      legs.push(delivery);
      return;
    }
    const spot = deliverySpot(order);
    const atSpot = spot
      ? legs.reduce((acc, key, i) => {
          if (key.endsWith('_DELIVERY') && deliverySpot(orderOf(key)) === spot) acc.push(i);
          return acc;
        }, [])
      : [];
    if (!atSpot.length) {
      legs.push(pickup, delivery);
      return;
    }
    legs.splice(atSpot[atSpot.length - 1] + 1, 0, delivery);
    legs.splice(atSpot[0], 0, pickup);
  });
  return legs;
}
